// The persistent shell (CONTRACT "Shell geometry"): CSS grid of top bar 44,
// tape 24 (collapsible to 0), body, status bar 24; columns rail 56, context
// list 256 (240 to 320, collapsible), fluid workspace, inspector 340 when
// open. Breakpoints in AppShell.css: 1280 the list undocks while the
// inspector is open, 1024 rail and list become the menu, 767 the inspector is
// a bottom sheet. The keyboard model, F6 regions, help sheet, toast and
// density live here; views render inside the workspace through children and
// reach the chrome through useShell().
import {
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ARCHIVE_PAGE_LIMIT } from '../api/config';
import {
  useAlertsSummary,
  useArchiveCalls,
  useSourceConnectors,
  useWorkspaceState,
} from '../api/hooks';
import type { AuthIdentity, WorkspaceRole } from '../api/types';
import { queryKeys } from '../api/queryKeys';
import { readAppliedDensity, setDensity, type DensityMode } from '../theme/density';
import { HelpSheet } from '../ui/HelpSheet';
import { Skeleton } from '../ui/Skeleton';
import { Toast } from '../ui/Toast';
import { isTextInput } from '../ui/isTextInput';
import { ContextList } from './ContextList';
import type { FirstValueFlags } from './FirstValueChecklist';
import { InspectorSlot } from './InspectorSlot';
import { MobileMenu } from './MobileMenu';
import { ProductRail } from './ProductRail';
import { ShellProvider, useShell } from './ShellContext';
import { StatusBar } from './StatusBar';
import { Tape } from './Tape';
import { TopBar } from './TopBar';
import type { RailGroupId } from './destinations';
import {
  REGION_ORDER,
  focusRegion,
  leaveTextField,
  nextRegion,
  visibleRegions,
  type Region,
} from './regions';
import { LIST_WIDTH, clampListWidth } from './shellPrefs';
import { deriveSourceRoutes, summariseSources } from './sourceRoutes';
import { KEY_HINTS, mergeKeyHints, useKeys } from './useKeys';
import './AppShell.css';

export interface AppShellProps {
  readonly identity: AuthIdentity | null;
  readonly accessRole?: WorkspaceRole | null;
  readonly children: ReactNode;
}

interface PreferencesPayload {
  readonly firstValue?: FirstValueFlags;
}

const EMPTY_PREFERENCES: PreferencesPayload = {};
const TAPE_FILTERS = { limit: ARCHIVE_PAGE_LIMIT.tape } as const;
// Three rows, the contract's skeleton shape (CONTRACT "States").
const SUSPENSE_SKELETON_ROWS = 3;

function ShellFrame({ identity, accessRole, children }: AppShellProps) {
  const shell = useShell();
  const queryClient = useQueryClient();
  const rootRef = useRef<HTMLDivElement>(null);
  const [region, setRegion] = useState<Region | null>(null);
  const [density, setDensityState] = useState<DensityMode>(() =>
    readAppliedDensity(document.documentElement),
  );
  const role = accessRole ?? identity?.role ?? null;

  const connectors = useSourceConnectors();
  const alerts = useAlertsSummary();
  const calls = useArchiveCalls(TAPE_FILTERS);
  const preferences = useWorkspaceState<PreferencesPayload>('preferences', {
    emptyPayload: EMPTY_PREFERENCES,
  });

  const routes = useMemo(() => deriveSourceRoutes(connectors.data), [connectors.data]);
  const sources = useMemo(() => {
    if (connectors.isPending)
      return {
        presence: 'off' as const,
        text: 'Sources: checking',
        detail: 'Waiting for source-connectors.',
      };
    if (connectors.isError && !connectors.data)
      return {
        presence: 'error' as const,
        text: 'Sources: unavailable',
        detail: 'source-connectors did not answer.',
      };
    return summariseSources(routes);
  }, [connectors.data, connectors.isError, connectors.isPending, routes]);
  const unread = useMemo(() => {
    const set = new Set<RailGroupId>();
    // Alerts lives in the Calls main since the D6 two-mains regrouping.
    if ((alerts.data?.unread ?? 0) > 0) set.add('calls');
    return set;
  }, [alerts.data?.unread]);

  // "Loaded records" for the search suggestions: the archive page the chrome
  // itself holds. A destination that loads more can replace this later; the
  // group states what it searched either way.
  const loadedRecords = useMemo(
    () => ({
      label: `the ${calls.rows.length} newest calls this browser holds`,
      rows: calls.rows,
    }),
    [calls.rows],
  );

  const onDensity = useCallback((mode: DensityMode) => {
    setDensity(
      document.documentElement,
      typeof localStorage === 'undefined' ? null : localStorage,
      mode,
    );
    setDensityState(mode);
  }, []);

  const onSignedOut = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.authMe() });
  }, [queryClient]);

  const cycleRegion = useCallback(
    (step: 1 | -1) => {
      const root = rootRef.current;
      if (!root) return;
      const regions = visibleRegions(root).map((entry) => entry.region);
      const next = nextRegion(region, step, regions);
      if (!next) return;
      setRegion(next);
      focusRegion(root, next);
    },
    [region],
  );

  // Escape leaving a text field used to call blur() and stop, which drops
  // focus on <body>: the next Tab restarted at the top of a 248 stop
  // sequence. Focus moves to the region the field belongs to instead, so the
  // order continues where the reader was.
  const onEscape = useCallback(() => {
    const active = document.activeElement;
    if (shell.helpOpen) {
      shell.setHelpOpen(false);
      return;
    }
    if (shell.menuOpen) {
      shell.setMenuOpen(false);
      return;
    }
    if (isTextInput(active) && active instanceof HTMLElement) {
      leaveTextField(active);
      return;
    }
    if (shell.inspectorOpen) {
      shell.closeInspector();
      return;
    }
    shell.keyHandlers.escape?.();
  }, [shell]);

  useKeys({
    ...shell.keyHandlers,
    escape: onEscape,
    list: shell.toggleList,
    inspector: shell.toggleInspector,
    tape: shell.toggleTape,
    help: () => shell.setHelpOpen(!shell.helpOpen),
    region: () => cycleRegion(1),
    regionBack: () => cycleRegion(-1),
    // "/" and Cmd K are handled by SearchField itself (slashFocus).
  });

  const firstValue = {
    connectedSources: routes.filter(
      (route) => route.presence === 'live' || route.presence === 'stale',
    ).length,
    matchingRecords: calls.coverage?.matchingRecords ?? null,
    alertRules: alerts.data?.rules ?? null,
    flags: preferences.payload?.firstValue ?? null,
  };

  // The stored drag wins everywhere; until the reader drags, the destination's
  // own default applies (Coin Checker docks at 320, CONTRACT "Context list").
  const listWidth = clampListWidth(
    shell.prefs.listWidth ?? shell.listContext?.width ?? LIST_WIDTH.initial,
  );

  // The "?" sheet prints the contract's table, with this destination's own
  // wording swapped in for the keys it rewords. One row per combination.
  const shortcuts = useMemo(
    () => mergeKeyHints(KEY_HINTS, shell.viewKeys.hints),
    [shell.viewKeys.hints],
  );

  return (
    <div
      ref={rootRef}
      className="shell"
      data-list={shell.listOpen ? 'open' : 'closed'}
      data-inspector={shell.inspectorOpen ? 'open' : 'closed'}
      data-tape={shell.tapeOpen ? 'open' : 'closed'}
      style={{ '--shell-list-w': `${listWidth}px` } as CSSProperties}
    >
      <TopBar
        identity={identity}
        accessRole={role}
        sources={sources}
        unreadAlerts={alerts.data?.unread ?? null}
        loadedRecords={loadedRecords}
        onSignedOut={onSignedOut}
      />
      <Tape />
      <ProductRail unread={unread} role={role} />
      <ContextList
        identity={identity}
        accessRole={role}
        routes={routes}
        routesState={
          connectors.isPending
            ? 'loading'
            : connectors.isError && !connectors.data
              ? 'error'
              : 'ready'
        }
        firstValue={firstValue}
        density={density}
        onDensity={onDensity}
        width={listWidth}
      />
      {/* The destination's code chunk is still arriving. The contract's
          Loading shape for a row surface is the skeleton, not the dashed
          block: at 900 ms the block filled 1100x100 of empty workspace with a
          Retry button and read as a failure, which it is not. The skeleton
          names the request and prints the seconds after 2 s. */}
      <main className="work" aria-label="Workspace" data-region="work" tabIndex={-1}>
        <Suspense
          fallback={
            <Skeleton
              className="work-skel"
              label="Loading this destination"
              rows={SUSPENSE_SKELETON_ROWS}
            />
          }
        >
          {children}
        </Suspense>
      </main>
      <InspectorSlot />
      <StatusBar identity={identity} accessRole={role} />
      <MobileMenu identity={identity} accessRole={role} sources={sources} />
      <HelpSheet
        open={shell.helpOpen}
        onClose={() => shell.setHelpOpen(false)}
        title="Keys"
        intro={`Single keys are inert while a text field has focus. F6 cycles ${REGION_ORDER.length} regions. Esc closes this sheet.`}
        shortcuts={shortcuts}
      />
      <Toast message={shell.toast.message} />
    </div>
  );
}

export function AppShell(props: AppShellProps) {
  return (
    <ShellProvider>
      <ShellFrame {...props} />
    </ShellProvider>
  );
}
